import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  locationLabel,
  useAdjustment,
  useUpdateAdjustmentNote,
  useVoidAdjustment,
  variantLabel,
  wipItemLabel,
} from '@/api/adjustments'
import type { AdjustmentLine, AdjustmentMovement } from '@/api/adjustments'
import { useAdjustmentAuditLogs } from '@/api/audit-logs'
import { useIdempotencyKey } from '@/api/idempotency'
import { poolLabel } from '@/api/pools'
import { useWorkspace } from '@/api/workspace'
import { DependentRecords } from '@/components/corrections/dependent-records'
import { correctionErrorMessage } from '@/components/corrections/error-message'
import { NoteCard } from '@/components/corrections/note-card'
import type { NoteSave } from '@/components/corrections/note-card'
import { VoidDialog } from '@/components/corrections/void-dialog'
import { VoidedBadge, VoidedNotice } from '@/components/corrections/voided'
import { voidedClass } from '@/components/corrections/voided-record'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney, formatQty } from '@/components/materials/pool-format'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordHistory } from '@/components/record-history/record-history'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

// One Adjustment (UI-SCREENS I-12, #67): its lines, the movements it posted with their value
// deltas (a WIP, Home or channel Adjustment is qty only, 04l #140 / 05e #173), who recorded it and on which business date. A posted record is corrected by voiding
// it (Owner, with a reason); only its note stays editable (#68). The Owner also sees its History
// (#69).
export const Route = createFileRoute('/w/$slug/adjustments/$adjustmentId/')({
  component: AdjustmentDetail,
  staticData: { breadcrumb: 'adjustments.detail.title' },
})

function AdjustmentDetail() {
  const { t } = useTranslation()
  const { slug, adjustmentId } = Route.useParams()
  const id = Number(adjustmentId)
  const adjustment = useAdjustment(slug, id)
  const isOwner = useWorkspace(slug).data?.role === 'owner'
  const history = useAdjustmentAuditLogs(slug, id, { enabled: isOwner })
  const voidAdjustment = useVoidAdjustment()
  const updateNote = useUpdateAdjustmentNote()
  const idempotency = useIdempotencyKey()

  if (adjustment.isPending) return <LoadingSkeleton variant="page" />
  if (adjustment.isError || !adjustment.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  const document = adjustment.data
  const voided = Boolean(document.voided_at)

  // One Idempotency-Key per submission; the void is a transition, so it carries one.
  const onVoid = (reason: string, done: () => void) => {
    const body = { void: { reason } }
    voidAdjustment.mutate(
      { params: { path: { slug, adjustment_id: id }, header: idempotency.headerFor(body) }, body },
      { onSuccess: done, onSettled: (_data, error) => idempotency.settle(error) },
    )
  }

  const onSaveNote = (note: string | null, save: NoteSave) => {
    updateNote.mutate(
      { params: { path: { slug, id } }, body: { adjustment: { note, lock_version: save.lockVersion } } },
      { onSuccess: save.done, onSettled: (_data, error) => save.settle(error) },
    )
  }

  return (
    <section className={`flex max-w-2xl flex-col gap-4 ${voidedClass(voided)}`}>
      <Link to="/w/$slug/adjustments" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('adjustments.back')}
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{formatBusinessDate(document.occurred_on)}</h1>
        <div className="flex items-center gap-3">
          <VoidedBadge record={document} />
          {document.can.void ? (
            <VoidDialog
              onVoid={onVoid}
              pending={voidAdjustment.isPending}
              errorMessage={voidAdjustment.isError ? correctionErrorMessage(t, voidAdjustment.error) : ''}
              blocked={voidAdjustment.isError ? <DependentRecords slug={slug} error={voidAdjustment.error} /> : null}
              onReset={voidAdjustment.reset}
            />
          ) : null}
        </div>
      </div>

      <Card>
        <CardContent className="flex flex-col gap-3 py-4">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
            <Figure label={t('adjustments.columns.location')} value={locationLabel(t, document.location)} />
            <Figure
              label={t('adjustments.columns.reason')}
              value={t(`adjustments.reasons.${adjustment.data.reason}`)}
            />
            <Figure
              label={t('adjustments.columns.createdBy')}
              value={
                adjustment.data.created_by
                  ? (adjustment.data.created_by.name ?? adjustment.data.created_by.email)
                  : '—'
              }
            />
          </dl>
          <VoidedNotice record={document} />
        </CardContent>
      </Card>

      <NoteCard
        note={document.note}
        lockVersion={document.lock_version}
        canEdit={document.can.update_note}
        pending={updateNote.isPending}
        errorMessage={updateNote.isError ? correctionErrorMessage(t, updateNote.error) : ''}
        onSave={onSaveNote}
        onReset={updateNote.reset}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('adjustments.form.lines')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="flex flex-col divide-y">
            {adjustment.data.lines.map((line) => (
              <li key={line.id} className="py-3 first:pt-0">
                <LineRow line={line} />
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('adjustments.detail.movements')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="flex flex-col divide-y">
            {adjustment.data.movements.map((movement) => (
              <li key={movement.id} className="py-3 first:pt-0">
                <MovementRow movement={movement} />
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {isOwner ? <RecordHistory entries={history.data} isPending={history.isPending} isError={history.isError} /> : null}
    </section>
  )
}

// A line or movement names a Pool (materials), a WIP item (its Model and colors, flagged when
// pre-vN) or a Variant (Home and channels, 05e #173). The pre-vN flag is the item's state today,
// not when the Adjustment was posted, and says so.
function ItemName({ row }: { row: Pick<AdjustmentLine, 'pool' | 'wip_part' | 'variant'> }) {
  const { t } = useTranslation()

  if (row.pool) return <span className="font-medium">{poolLabel(row.pool)}</span>
  if (row.variant) return <span className="font-medium">{variantLabel(row.variant)}</span>
  if (!row.wip_part) return <span className="font-medium">—</span>
  return (
    <span className="flex flex-wrap items-center gap-2 font-medium">
      {wipItemLabel(row.wip_part)}
      {row.wip_part.pre_vn ? (
        <Badge variant="outline" title={t('adjustments.preVnNowHint')}>
          {t('adjustments.preVnNow')}
        </Badge>
      ) : null}
    </span>
  )
}

function LineRow({ line }: { line: AdjustmentLine }) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-1">
      <ItemName row={line} />
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <Figure
          label={t('adjustments.form.countedQty')}
          value={line.pool ? `${formatQty(line.counted_qty)} ${line.pool.unit}` : formatQty(line.counted_qty)}
        />
        <Figure label={t('adjustments.form.qtyDelta')} value={signedQty(line.qty_delta)} />
      </dl>
    </div>
  )
}

// One movement per Pool, with the value the count moved (a WIP movement has none). A count that
// only corrects value carries qty_delta 0 (decided Q-4). A void adds the exact negation of each row, linked to it
// by `reverses_movement_id` (UI-SCREENS §2).
function MovementRow({ movement }: { movement: AdjustmentMovement }) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <ItemName row={movement} />
        {movement.reverses_movement_id ? (
          <Badge variant="outline">{t('corrections.reversalOf', { id: movement.reverses_movement_id })}</Badge>
        ) : null}
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <Figure label={t('materials.movements.columns.qty')} value={signedQty(movement.qty_delta)} />
        {movement.value_delta === null ? null : (
          <Figure label={t('materials.movements.columns.value')} value={signedMoney(movement.value_delta)} />
        )}
      </dl>
    </div>
  )
}

const signedQty = (value: number) => `${value > 0 ? '+' : value < 0 ? '−' : ''}${formatQty(Math.abs(value))}`
const signedMoney = (value: number) =>
  `${value > 0 ? '+' : value < 0 ? '−' : ''}Rp ${formatMoney(Math.abs(value))}`

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}
